
import Vue from 'vue'
import ErrorHandler from "../../../services/error-handler";
import RespressoApi from "../../../api/respresso-api";
import {UserQuestion, UserQuestions, UserQuestionSelect, UserQuestionUrgency} from "respresso";
import {SlimSelectOption} from "../vue-slim-select/vue-slim-select";
import {settingsModule} from "../../../store/modules/settings";
import settingsService from "../../../services/settings-service";

const emptyAnswerKey = "__please_select__";
const invalidAnswerKeys = [emptyAnswerKey];

export default Vue.extend({
  name: "question-banner",
  async mounted() {
    if ((await settingsService.getAuthSettingsAsync())?.onPremise) {
      // Do not load questions for on-premise installations
    } else {
      await this.loadQuestions();
    }
  },
  data() {
    return {
      show: false as boolean,
      loading: false as boolean,
      question: null as (UserQuestion | null),
      allowHide: false as boolean,
      allowDeny: false as boolean,
      blocking: false as boolean,
      error: null as string | null,

      selectOptions: {} as { [key: string]: string },
      answers: [] as string[],
    }
  },
  computed: {
    questionAsSelect(): UserQuestionSelect {
      return this.question as UserQuestionSelect;
    },
    questionSelectPlaceholder(): string {
      return this.$t(this.questionAsSelect.notSelectedLabel || "Please select") as string;
    },
    questionSelectOptions() {
      let options: SlimSelectOption[] = [];
      let hasSelected = false;
      for (let optionKey in this.selectOptions) {
        if (this.selectOptions.hasOwnProperty(optionKey)) {
          const optionLabel = this.selectOptions[optionKey];
          const selected = this.answers.includes(optionKey);
          hasSelected = hasSelected || selected;
          options.push({
            value: optionKey,
            text: this.$t(optionLabel) as string,
            selected: selected,
            disabled: invalidAnswerKeys.includes(optionKey),
          })
        }
      }
      if (!hasSelected && !this.questionAsSelect.multiSelect) {
        options = [
          {
            value: emptyAnswerKey,
            text: this.questionSelectPlaceholder,
            selected: true,
          },
          ...options,
        ]
      }
      return options;
    }
  },
  methods: {
    loadQuestions: async function() {
      this.loading = true;
      const questions = await ErrorHandler.tryRequest(() => RespressoApi.getQuestions());
      this.loading = false;
      this.updateQuestions(questions);
    },
    updateQuestions: function(questions: UserQuestions | null) {
      // reverted order to avoid -1 edge case for unknown urgency
      // -> largest index wins -> unknown urgency won't be handles as most important
      const urgencyOrder: UserQuestionUrgency[] = ["unimportant", "bestEffort", "required", "important"]
      let questionToShow: UserQuestion | null = null;
      if (questions) {
        let questionUrgencyToShow: number = -1;
        if (questions.questions.length > 0) {
          for (let question of questions.questions) {
            const urgency = urgencyOrder.indexOf(question.urgency)
            if (!questionToShow || urgency > questionUrgencyToShow) {
              questionToShow = question;
              questionUrgencyToShow = urgency;
            }
          }
        }
      }
      this.question = questionToShow;
      this.answers = [];
      this.selectOptions = {};
      if (this.question) {
        if (this.question.urgency === "required") {
          this.show = true;
          this.allowHide = false;
          this.allowDeny = false;
          this.blocking = true;
        } else if (this.question.urgency === "important") {
          this.show = true;
          this.allowHide = true;
          this.allowDeny = false;
          this.blocking = false;
        } else if (this.question.urgency === "bestEffort") {
          this.show = true;
          this.allowHide = true;
          this.allowDeny = true;
          this.blocking = false;
        } else {
          // unimportant questions are not shown by default -> only when it's the cost to proceed with an action
          this.show = false;
        }

        if (this.question.type === "select") {
          // FIXME remove comments bellow - used for manual testing only
          //this.questionAsSelect.multiSelect = true;
          //this.questionAsSelect.addable = true;
          if (this.questionAsSelect.defaultValue) {
            this.answers = [this.questionAsSelect.defaultValue];
          }
          this.selectOptions = {...this.questionAsSelect.options};
        }
      } else {
        this.show = false;
      }
      // FIXME remove comments bellow - used for manual testing only
      //this.blocking = false;
      //this.allowHide = false;
      //this.allowDeny = true;
      //this.loading = true;
    },
    denyQuestion: async function() {
      const id = this.question?.id;
      if (id) {
        this.loading = true;
        const questions = await ErrorHandler.tryRequest(() => RespressoApi.denyQuestion(id));
        this.loading = false;
        if(questions) {
          this.updateQuestions(questions);
        }
      }
    },
    onSelectionChanged(items: SlimSelectOption[] | SlimSelectOption) {
      if (Array.isArray(items)) {
        this.answers = items.map(it => it.selected ? it.value || it.text : null).filter(it => it != null) as string[];
      } else {
        this.answers = [items.value || ""];
      }
      this.validate();
    },
    onSelectionItemAddedByUser(item: SlimSelectOption) {
      if((item.value && this.selectOptions.hasOwnProperty(item.value)) || !this.questionAsSelect.addable){
        return;
      }
      this.selectOptions = Object.assign(this.selectOptions, {
        [item.text]: item.text
      })
    },
    saveAnswer: async function() {
      const questionId = this.question?.id;
      const answers = this.validate();
      if (questionId && answers) {
        this.loading = true;
        const questions = await ErrorHandler.tryRequest(() => RespressoApi.answerQuestion({
          id: questionId,
          answers: answers,
        }));
        this.loading = false;
        if(questions) {
          this.updateQuestions(questions);
        } else {
          this.error = "question.error.invalidAnswer"
        }
      }
    },
    validate(): string[] | null {
      if (this.answers && this.answers.length > 0 && !this.answers.find(it => !it || invalidAnswerKeys.includes(it))) {
        this.error = null;
        return this.answers;
      } else {
        this.error = "question.error.missingAnswer"
        return null;
      }
    }

  }
})
